import styled, {keyframes} from 'styled-components/macro'
import {getColor} from "@startlibs/utils"
import {Icon} from "@startlibs/components"
import {darken, transparentize} from "polished"
import {AttachmentIcon} from "../../AttachmentBoxStyles"

export const dotsAnim = keyframes`
  0%   { content: '.'; }
  25%  { content: '..'; }
  50%  { content: '...'; }
  75%  { content: '..'; }
  100% { content: '.'; }
`

export const fadeSlideIn = keyframes`
  from {
    opacity: 0;
    transform: translateY(6px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`

// ── AI loading state ─────────────────────────────────────────────────────────

export const AiProcessingWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
`

export const AiProcessingLabel = styled.div`
  font-size: 12px;
  font-weight: 600;
  color: ${props => getColor('main')(props)};
  display: flex;
  align-items: baseline;
  gap: 0;
`

export const AnimatedDots = styled.span`
  display: inline-block;
  min-width: 18px;
  &::after {
    content: '.';
    animation: ${dotsAnim} 800ms steps(1, end) infinite;
  }
`

// ── AI Summary panel ─────────────────────────────────────────────────────────

export const AiSummaryWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 10px;
  animation: ${keyframes`from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}`} 400ms ease-out;
`

export const AiSummaryHeading = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  ${Icon} {
    font-size: 16px;
    color: #999999;
  }
`

export const AiSummaryScrollArea = styled.div`
  min-height: 216px;
  max-height: 425px;
  overflow-y: auto;
  display: flex;
  flex-direction: column;
  gap: 10px;
  padding-right: 6px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.28) transparent;
  &::-webkit-scrollbar {
    width: 6px;
  }
  &::-webkit-scrollbar-track {
    background: transparent;
  }
  &::-webkit-scrollbar-thumb {
    min-height: 22px;
    background: rgba(0,0,0,0.24);
    border-radius: 4px;
  }
`

export const AiSummaryScrollOuter = styled.div`
  position: relative;
  max-height: 425px;
`

export const AiSummaryScrollFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  width: 475px;
  height: 43px;
  background: linear-gradient(180deg, ${props => transparentize(1, getColor('recordDetailsBg')(props))} 0%, ${props => getColor('recordDetailsBg')(props)} 100%);
  pointer-events: none;
  z-index: 1;
`

export const AiSummaryText = styled.p`
  font-size: 12px;
  color: rgba(0,0,0,0.75);
  line-height: 1.55;
  margin: 0;
`

// ── Structured Summary styled components ─────────────────────────────────────

export const SummaryTitle = styled.h3`
  font-size: 13px;
  font-weight: 600;
  color: rgba(0,0,0,0.85);
  margin: 0 0 10px 0;
  line-height: 1.4;
  font-family: "Open Sans", sans-serif;
`

export const SummarySectionTitle = styled.h4`
  font-size: 12px;
  font-weight: 600;
  color: rgba(0,0,0,0.8);
  margin: 14px 0 6px 0;
  line-height: 1.4;
  font-family: "Open Sans", sans-serif;
  &:first-child {
    margin-top: 0;
  }
`

export const SummarySubsectionTitle = styled.h5`
  font-size: 11.5px;
  font-weight: 600;
  color: rgba(0,0,0,0.7);
  margin: 10px 0 4px 0;
  line-height: 1.4;
  font-family: "Open Sans", sans-serif;
`

export const SummaryParagraph = styled.p`
  font-size: 12px;
  color: rgba(0,0,0,0.75);
  line-height: 1.55;
  margin: 0 0 8px 0;
`

export const SummaryKeyValue = styled.div`
  font-size: 12px;
  color: rgba(0,0,0,0.75);
  line-height: 1.55;
  margin: 0 0 6px 0;
`

export const SummaryKeyLabel = styled.span`
  font-weight: 600;
  color: rgba(0,0,0,0.8);
`

export const SummaryBulletList = styled.ul`
  margin: 4px 0 8px 0;
  padding-left: 18px;
  list-style: disc;
`

export const SummaryBulletItem = styled.li`
  font-size: 12px;
  color: rgba(0,0,0,0.75);
  line-height: 1.55;
  margin-bottom: 4px;
`

// ── Source image (citation thumbnails) ───────────────────────────────────────

export const SourceImageWrap = styled.div`
  display: inline-block;
  margin: 6px 0;
`

export const SourceImage = styled.img`
  max-width: 100%;
  max-height: 240px;
  border-radius: 4px;
  border: 1px solid rgba(0,0,0,0.1);
  display: block;
`

export const SourceImageCaption = styled.div`
  font-size: 11px;
  color: rgba(0,0,0,0.55);
  margin-top: 4px;
  font-style: italic;
`

// ── Relevance / patient-match score bar ──────────────────────────────────────

export const RelevanceBadgeWrap = styled.div`
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  min-width: 149px;
  width: 149px;
`

export const RelevanceLabel = styled.div`
  display: flex;
  align-items: flex-end;
  gap: 4px;
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
  ${Icon} {
    font-size: 16px;
    color: #999999;
  }
`

export const RelevanceBarRow = styled.div`
  display: flex;
  align-items: flex-start;
  gap: 6px;
  width: 100%;
`

export const RelevanceBarLabels = styled.div`
  display: flex;
  justify-content: space-between;
  width: 149px;
  margin-top: 3px;
`

export const RelevanceBarLabel = styled.span`
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: normal;
`

export const RelevancyExplanationChip = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 16px;
  height: 16px;
  padding: 0;
  margin-left: 4px;
  margin-top: 4px;
  background: transparent;
  border: 1.5px solid ${props => getColor('main')(props)};
  border-radius: 50%;
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-weight: 700;
  color: ${props => getColor('main')(props)};
  cursor: pointer;
  transition: background 150ms;
  flex-shrink: 0;

  &:hover {
    background: ${props => transparentize(0.92, getColor('main')(props))};
  }
`

export const RelevancyExplanationPopover = styled.div`
  background: white;
  border: 1px solid #e0e0e0;
  border-radius: 6px;
  box-shadow: 0 4px 16px rgba(0, 0, 0, 0.15);
  padding: 12px;
  animation: ${fadeSlideIn} 200ms ease-out;

  &::before {
    content: '';
    position: absolute;
    ${props => props.flipped ? `
      top: 100%;
      right: 12px;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-top: 6px solid #e0e0e0;
    ` : `
      bottom: 100%;
      right: 12px;
      border-left: 6px solid transparent;
      border-right: 6px solid transparent;
      border-bottom: 6px solid #e0e0e0;
    `}
    width: 0;
    height: 0;
  }

  &::after {
    content: '';
    position: absolute;
    ${props => props.flipped ? `
      top: calc(100% - 1px);
      right: 13px;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-top: 5px solid white;
    ` : `
      bottom: calc(100% - 1px);
      right: 13px;
      border-left: 5px solid transparent;
      border-right: 5px solid transparent;
      border-bottom: 5px solid white;
    `}
    width: 0;
    height: 0;
  }
`

export const AboutAiButton = styled.button`
  display: inline-flex;
  align-items: center;
  padding: 2px 2px 3px;
  border: 1px solid transparent;
  border-radius: 4px;
  background: transparent;
  color: ${props => getColor('main')(props)};
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  white-space: nowrap;
  cursor: pointer;
  :hover {
    color: ${props => darken(0.1, getColor('main')(props))};
  }
`

export const RelevancyExplanationText = styled.p`
  font-family: "Open Sans", sans-serif;
  font-size: 11px;
  color: #444444;
  line-height: 1.5;
  margin: 0;
`

export const ExplanationRatingRow = styled.div`
  display: flex;
  align-items: center;
  gap: 6px;
  margin-top: 2px;
`

export const ExplanationRatingLabel = styled.span`
  font-size: 10px;
  font-weight: 400;
  color: #999999;
  font-family: "Open Sans", sans-serif;
`

export const ExplanationThumbsRow = styled.div`
  display: flex;
  gap: 4px;
`

export const ExplanationThumbBtn = styled.button`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 18px;
  height: 18px;
  padding: 2px;
  border: none;
  border-radius: 50%;
  background: transparent;
  cursor: pointer;
  transition: background 150ms;
  color: #b3b3b3;

  &:hover {
    background: ${props => transparentize(0.92, getColor('main')(props))};
  }
`

// ── Patient mismatch section ─────────────────────────────────────────────────

export const PatientMismatchWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 14px;
  padding-left: 3px;
`

export const PatientMismatchLabel = styled.div`
  display: flex;
  align-items: center;
  gap: 4px;
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: normal;
  ${Icon} {
    font-size: 16px;
    color: #999999;
  }
`

export const PatientMismatchBody = styled.p`
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 400;
  line-height: 20px;
  margin: 0;
`

export const PatientMismatchGray = styled.span`
  color: #444444;
`

export const PatientMismatchBold = styled.span`
  color: #000000;
  font-weight: 700;
`

// ── AI error state ───────────────────────────────────────────────────────────

export const AiErrorWrap = styled.div`
  display: flex;
  flex-direction: column;
  gap: 6px;
  animation: ${keyframes`from{opacity:0;transform:translateY(4px)}to{opacity:1;transform:translateY(0)}`} 400ms ease-out;
`

export const AiErrorHeading = styled.div`
  display: flex;
  // align-items: flex-start;
  align-items: ${props => props.alignEnd ? 'flex-end' : 'flex-start'};
  gap: 4px;
  // color: ${props => getColor('alert')(props)};
  color: #999999;
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  // font-weight: 600;
  ${Icon} {
    font-size: 16px;
    margin-top: 2px;
    color: #999999;
    // color: ${props => getColor('alert')(props)};
  }
`

export const AiErrorText = styled.p`
  font-size: 12px;
  color: rgba(0,0,0,0.5);
  line-height: 1.55;
  margin: 0;
`

export const TryAgainLink = styled.a`
  display: inline-flex;
  align-items: center;
  color: #008DD0;
  text-decoration: none !important;
  font-size: 12px;
  font-family: 'Open Sans', sans-serif;
  cursor: pointer;
  font-weight: 500;
  &:hover { text-decoration: none !important; opacity: 0.8; }
  ${Icon} {
    font-size: 15px;
  }
`

// ── "Open outside" button ────────────────────────────────────────────────────

export const OpenOutsideButton = styled.button`
  display: inline-flex;
  align-items: center;
  gap: 6px;
  margin-top: 4px;
  align-self: flex-end;
  padding: 5px 10px;
  border-radius: 0px;
  border: 0px solid rgba(0,141,208,0.35);
  background: transparent;
  color: ${props => getColor('main')(props)};
  font-family: "Open Sans", sans-serif;
  font-size: 12px;
  font-weight: 500;
  cursor: pointer;
  line-height: normal;
  :hover {
    color: ${props => darken(0.1, getColor('main')(props))};
  }
  :active {
  }
`

// ── Modal styled components ──────────────────────────────────────────────────

export const ModalRecordBadge = styled.div`
  display: inline-flex;
  -webkit-box-align: center;
  gap: 7px;
  border-radius: 4px;
  background: rgb(246, 251, 254);
  border: 1px solid rgba(0, 141, 208, 0.15);
  padding: 0px 6px;
  margin-left: 16px;
  align-self: flex-start;
  align-content: center;
  flex-direction: row;
  justify-content: center;
  max-height: 22px;
`

export const ModalRecordBadgeLabel = styled.span`
  display: inline-flex;
  align-items: center;
  gap: 4px;
  color: var(--Neutrals-Gray-150, #5A5A5A);
  font-family: "Open Sans", sans-serif;
  font-size: 10px;
  font-style: normal;
  font-weight: 400;
  line-height: 10px;
  white-space: nowrap;
`

export const ModalRecordBadgeIcon = styled(AttachmentIcon)`
  display: inline-flex;
  align-items: center;
  justify-content: center;
  position: static;
  font-size: 10px;
  width: 10px;
  height: 10px;
  line-height: 10px;
  vertical-align: middle;
  top: auto;
  left: auto;
  text-align: center;
  flex-shrink: 0;
  color: ${props => getColor('main')(props)};
`

export const ModalSummaryScrollOuter = styled.div`
  position: relative;
  margin-top: 16px;
`

export const ModalSummaryScroll = styled.div`
  overflow-y: auto;
  min-height: 312px;
  max-height: calc(100vh - 280px);
  display: flex;
  flex-direction: column;
  gap: 12px;
  padding-right: 8px;
  scrollbar-width: thin;
  scrollbar-color: rgba(0,0,0,0.24) transparent;
  &::-webkit-scrollbar { width: 6px; }
  &::-webkit-scrollbar-track { background: transparent; }
  &::-webkit-scrollbar-thumb { min-height: 22px; background: rgba(0,0,0,0.24); border-radius: 4px; }
`

export const ModalSummaryScrollFade = styled.div`
  position: absolute;
  bottom: 0;
  left: 0;
  right: 0;
  height: 48px;
  background: linear-gradient(180deg, rgba(255,255,255,0) 0%, #fff 100%);
  pointer-events: none;
  z-index: 1;
`

export const ModalSummaryScrollTopFade = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 32px;
  background: linear-gradient(180deg, #fff 0%, rgba(255,255,255,0) 100%);
  pointer-events: none;
  z-index: 1;
`
